import { definePlatformSetting } from '@crest/platform-contracts/settings';
import { z } from 'zod';
import { documentsProtectedFormsCopySetting, documentsProtectedFormsDisplaySetting, } from './documents-protected-form-setting-definitions.js';
import { documentsSignedPoliciesCopySetting, documentsSignedPoliciesDisplaySetting, } from './documents-signed-policy-setting-definitions.js';
export * from './documents-protected-form-setting-definitions.js';
export * from './documents-signed-policy-setting-definitions.js';
export const documentsTemplatesConfigurationValueSchema = z.strictObject({
    maximumTemplates: z.union([z.literal(100), z.literal(250), z.literal(500), z.literal(1_000)]),
    maximumVariablesPerTemplate: z.number().int().min(1).max(100),
    maximumBodyBytes: z.number().int().min(10_000).max(500_000),
    defaultCategory: z.string().regex(/^[a-z][a-z0-9]*(?:[-_][a-z0-9]+)*$/).max(80),
    defaultSensitivity: z.enum(['ordinary', 'confidential', 'restricted']),
    defaultPageFormat: z.enum(['letter', 'legal', 'a4']),
});
export const documentsTemplatesConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'documents.templates.configuration',
        moduleId: 'documents',
        title: 'Document template behavior',
        helpText: 'Controls tenant template limits and safe defaults within coded platform limits.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            maximumTemplates: 500,
            maximumVariablesPerTemplate: 100,
            maximumBodyBytes: 500_000,
            defaultCategory: 'general',
            defaultSensitivity: 'ordinary',
            defaultPageFormat: 'letter',
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: false,
        safetyClass: 'ordinary',
    },
    valueSchema: documentsTemplatesConfigurationValueSchema,
});
export const documentsTemplatesDisplayValueSchema = z.strictObject({
    pageSize: z.union([z.literal(25), z.literal(50), z.literal(100)]),
    density: z.enum(['comfortable', 'compact']),
    showVersionMetadata: z.boolean(),
    showSensitivity: z.boolean(),
    editorWidth: z.enum(['standard', 'wide', 'full']),
    previewScale: z.union([z.literal(75), z.literal(90), z.literal(100)]),
});
export const documentsTemplatesDisplaySetting = definePlatformSetting({
    definition: {
        key: 'documents.templates.display',
        moduleId: 'documents',
        title: 'Document template display',
        helpText: 'Controls tenant and user display preferences for the template library and preview.',
        configurationDomain: 'layout',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            pageSize: 50,
            density: 'comfortable',
            showVersionMetadata: true,
            showSensitivity: true,
            editorWidth: 'wide',
            previewScale: 90,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: true,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: documentsTemplatesDisplayValueSchema,
});
const documentsTemplateStatusLabelsSchema = z.strictObject({
    draft: z.string().trim().min(1).max(60),
    published: z.string().trim().min(1).max(60),
    archived: z.string().trim().min(1).max(60),
});
const documentsTemplatePurposeLabelsSchema = z.strictObject({
    contract: z.string().trim().min(1).max(60),
    notice: z.string().trim().min(1).max(60),
    letter: z.string().trim().min(1).max(60),
    form: z.string().trim().min(1).max(60),
    certificate: z.string().trim().min(1).max(60),
    other: z.string().trim().min(1).max(60),
});
const documentsTemplateSensitivityLabelsSchema = z.strictObject({
    ordinary: z.string().trim().min(1).max(60),
    confidential: z.string().trim().min(1).max(60),
    restricted: z.string().trim().min(1).max(60),
});
const documentsTemplatePageFormatLabelsSchema = z.strictObject({
    letter: z.string().trim().min(1).max(60),
    legal: z.string().trim().min(1).max(60),
    a4: z.string().trim().min(1).max(60),
});
const documentsTemplateOrientationLabelsSchema = z.strictObject({
    portrait: z.string().trim().min(1).max(60),
    landscape: z.string().trim().min(1).max(60),
});
const documentsTemplateMarginLabelsSchema = z.strictObject({
    compact: z.string().trim().min(1).max(60),
    standard: z.string().trim().min(1).max(60),
    wide: z.string().trim().min(1).max(60),
});
export const documentsTemplatesCopyValueSchema = z.strictObject({
    navigationLabel: z.string().trim().min(1).max(60),
    title: z.string().trim().min(1).max(100),
    description: z.string().trim().min(1).max(300),
    createAction: z.string().trim().min(1).max(80),
    appendVersionAction: z.string().trim().min(1).max(80),
    previewAction: z.string().trim().min(1).max(80),
    publishAction: z.string().trim().min(1).max(80),
    archiveAction: z.string().trim().min(1).max(80),
    restoreAction: z.string().trim().min(1).max(80),
    rollbackAction: z.string().trim().min(1).max(80),
    keyLabel: z.string().trim().min(1).max(80),
    nameLabel: z.string().trim().min(1).max(80),
    categoryLabel: z.string().trim().min(1).max(80),
    purposeLabel: z.string().trim().min(1).max(80),
    bodyLabel: z.string().trim().min(1).max(80),
    variablesLabel: z.string().trim().min(1).max(80),
    previewValuesLabel: z.string().trim().min(1).max(80),
    sensitivityLabel: z.string().trim().min(1).max(80),
    pageFormatLabel: z.string().trim().min(1).max(80),
    orientationLabel: z.string().trim().min(1).max(80),
    marginLabel: z.string().trim().min(1).max(80),
    signatureRequirementLabel: z.string().trim().min(1).max(180),
    consentLabel: z.string().trim().min(1).max(80),
    reasonLabel: z.string().trim().min(1).max(80),
    loadingLabel: z.string().trim().min(1).max(120),
    savingLabel: z.string().trim().min(1).max(120),
    loadError: z.string().trim().min(1).max(240),
    mutationError: z.string().trim().min(1).max(240),
    previewMissingLabel: z.string().trim().min(1).max(180),
    previewFrameTitle: z.string().trim().min(1).max(120),
    emptyTitle: z.string().trim().min(1).max(120),
    emptyDescription: z.string().trim().min(1).max(300),
    statusLabels: documentsTemplateStatusLabelsSchema,
    purposeLabels: documentsTemplatePurposeLabelsSchema,
    sensitivityLabels: documentsTemplateSensitivityLabelsSchema,
    pageFormatLabels: documentsTemplatePageFormatLabelsSchema,
    orientationLabels: documentsTemplateOrientationLabelsSchema,
    marginLabels: documentsTemplateMarginLabelsSchema,
});
export const documentsTemplatesCopySetting = definePlatformSetting({
    definition: {
        key: 'documents.copy.templates',
        moduleId: 'documents',
        title: 'Document template copy',
        helpText: 'Controls tenant-editable labels for document templates, versions, and preview.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            navigationLabel: 'Document templates',
            title: 'Document templates',
            description: 'Create immutable template versions and preview exact variable substitution before publishing.',
            createAction: 'Create template',
            appendVersionAction: 'Save new version',
            previewAction: 'Preview document',
            publishAction: 'Publish version',
            archiveAction: 'Archive template',
            restoreAction: 'Restore template',
            rollbackAction: 'Roll back as new version',
            keyLabel: 'Template key',
            nameLabel: 'Template name',
            categoryLabel: 'Category',
            purposeLabel: 'Purpose',
            bodyLabel: 'Safe HTML body',
            variablesLabel: 'Variable definitions (JSON)',
            previewValuesLabel: 'Preview values (JSON)',
            sensitivityLabel: 'Sensitivity',
            pageFormatLabel: 'Page format',
            orientationLabel: 'Orientation',
            marginLabel: 'Margins',
            signatureRequirementLabel: 'Requires consent and a controlled signature placeholder',
            consentLabel: 'Signature consent text',
            reasonLabel: 'Change reason',
            loadingLabel: 'Loading document templates…',
            savingLabel: 'Saving document template…',
            loadError: 'Document templates could not be loaded.',
            mutationError: 'The document template change could not be saved. Refresh and try again.',
            previewMissingLabel: 'Required preview values are still missing.',
            previewFrameTitle: 'Sanitized document template preview',
            emptyTitle: 'No document templates yet',
            emptyDescription: 'Create a draft, verify the preview, and publish the reviewed version when ready.',
            statusLabels: { draft: 'Draft', published: 'Published', archived: 'Archived' },
            purposeLabels: {
                contract: 'Contract', notice: 'Notice', letter: 'Letter', form: 'Form',
                certificate: 'Certificate', other: 'Other',
            },
            sensitivityLabels: {
                ordinary: 'Ordinary', confidential: 'Confidential', restricted: 'Restricted',
            },
            pageFormatLabels: { letter: 'Letter', legal: 'Legal', a4: 'A4' },
            orientationLabels: { portrait: 'Portrait', landscape: 'Landscape' },
            marginLabels: { compact: 'Compact', standard: 'Standard', wide: 'Wide' },
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: documentsTemplatesCopyValueSchema,
});
export const documentsInstancesConfigurationValueSchema = z.strictObject({
    defaultExpirationDays: z.number().int().min(1).max(365),
    maximumExpirationDays: z.number().int().min(1).max(365),
    requireEmailForRelease: z.boolean(),
    allowReminderIntents: z.boolean(),
}).superRefine((value, context) => {
    if (value.defaultExpirationDays > value.maximumExpirationDays) {
        context.addIssue({
            code: 'custom',
            path: ['defaultExpirationDays'],
            message: 'Default expiration may not exceed the maximum.',
        });
    }
});
export const documentsInstancesConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'documents.instances.configuration',
        moduleId: 'documents',
        title: 'Document instance behavior',
        helpText: 'Controls draft expiration and provider-neutral release/reminder preparation.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            defaultExpirationDays: 30,
            maximumExpirationDays: 365,
            requireEmailForRelease: true,
            allowReminderIntents: true,
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: false,
        safetyClass: 'ordinary',
    },
    valueSchema: documentsInstancesConfigurationValueSchema,
});
export const documentsInstancesDisplayValueSchema = z.strictObject({
    pageSize: z.union([z.literal(25), z.literal(50), z.literal(100)]),
    density: z.enum(['comfortable', 'compact']),
    showRecipientContact: z.boolean(),
    showIntegrityHashes: z.boolean(),
    previewScale: z.union([z.literal(75), z.literal(90), z.literal(100)]),
});
export const documentsInstancesDisplaySetting = definePlatformSetting({
    definition: {
        key: 'documents.instances.display',
        moduleId: 'documents',
        title: 'Document instance display',
        helpText: 'Controls tenant and user layout choices for draft, sent, and signed documents.',
        configurationDomain: 'layout',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            pageSize: 50,
            density: 'comfortable',
            showRecipientContact: true,
            showIntegrityHashes: false,
            previewScale: 90,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: true,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: documentsInstancesDisplayValueSchema,
});
const documentsInstanceStatusLabelsSchema = z.strictObject({
    draft: z.string().trim().min(1).max(60),
    sent: z.string().trim().min(1).max(60),
    viewed: z.string().trim().min(1).max(60),
    signed: z.string().trim().min(1).max(60),
    declined: z.string().trim().min(1).max(60),
    voided: z.string().trim().min(1).max(60),
    expired: z.string().trim().min(1).max(60),
});
export const documentsInstancesCopyValueSchema = z.strictObject({
    navigationLabel: z.string().trim().min(1).max(60),
    title: z.string().trim().min(1).max(100),
    description: z.string().trim().min(1).max(300),
    createDraftAction: z.string().trim().min(1).max(80),
    printablePreviewAction: z.string().trim().min(1).max(80),
    sendAction: z.string().trim().min(1).max(80),
    remindAction: z.string().trim().min(1).max(80),
    voidAction: z.string().trim().min(1).max(80),
    expireAction: z.string().trim().min(1).max(80),
    templateLabel: z.string().trim().min(1).max(80),
    recipientNameLabel: z.string().trim().min(1).max(80),
    recipientEmailLabel: z.string().trim().min(1).max(80),
    recipientPhoneLabel: z.string().trim().min(1).max(80),
    valuesLabel: z.string().trim().min(1).max(80),
    expiresLabel: z.string().trim().min(1).max(80),
    reasonLabel: z.string().trim().min(1).max(80),
    statusLabel: z.string().trim().min(1).max(80),
    loadingLabel: z.string().trim().min(1).max(120),
    savingLabel: z.string().trim().min(1).max(120),
    loadError: z.string().trim().min(1).max(240),
    mutationError: z.string().trim().min(1).max(240),
    emptyTitle: z.string().trim().min(1).max(120),
    emptyDescription: z.string().trim().min(1).max(300),
    statusLabels: documentsInstanceStatusLabelsSchema,
});
export const documentsInstancesCopySetting = definePlatformSetting({
    definition: {
        key: 'documents.copy.instances',
        moduleId: 'documents',
        title: 'Document instance copy',
        helpText: 'Controls tenant-editable labels for generated drafts and their lifecycle.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            navigationLabel: 'Documents',
            title: 'Generated documents',
            description: 'Generate exact drafts, release signing requests, and retain immutable lifecycle evidence.',
            createDraftAction: 'Generate draft',
            printablePreviewAction: 'Printable preview',
            sendAction: 'Release for delivery',
            remindAction: 'Prepare reminder',
            voidAction: 'Void document',
            expireAction: 'Expire document',
            templateLabel: 'Published template',
            recipientNameLabel: 'Recipient name',
            recipientEmailLabel: 'Recipient email',
            recipientPhoneLabel: 'Recipient phone',
            valuesLabel: 'Document values (JSON)',
            expiresLabel: 'Expires at',
            reasonLabel: 'Reason',
            statusLabel: 'Status',
            loadingLabel: 'Loading generated documents…',
            savingLabel: 'Saving document…',
            loadError: 'Generated documents could not be loaded.',
            mutationError: 'The document action could not be completed. Refresh and try again.',
            emptyTitle: 'No generated documents yet',
            emptyDescription: 'Choose a published template and generate an exact draft without sending it.',
            statusLabels: {
                draft: 'Draft', sent: 'Sent', viewed: 'Viewed', signed: 'Signed',
                declined: 'Declined', voided: 'Voided', expired: 'Expired',
            },
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: documentsInstancesCopyValueSchema,
});
const documentsTrainingRecordPageSchema = z.strictObject({
    format: z.enum(['letter', 'legal', 'a4']),
    orientation: z.enum(['portrait', 'landscape']),
    marginPreset: z.enum(['compact', 'standard', 'wide']),
});
export const documentsTrainingRecordsConfigurationValueSchema = z.strictObject({
    maximumInternalCompletions: z.union([
        z.literal(50), z.literal(100), z.literal(250), z.literal(500),
    ]),
    maximumProfessionalCertifications: z.union([
        z.literal(25), z.literal(50), z.literal(100), z.literal(250),
    ]),
    includeProfessionalCertificationsByDefault: z.boolean(),
    transcriptPage: documentsTrainingRecordPageSchema,
    certificatePage: documentsTrainingRecordPageSchema,
});
export const documentsTrainingRecordsConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'documents.training-records.configuration',
        moduleId: 'documents',
        title: 'Training record document behavior',
        helpText: 'Controls bounded transcript content and printable page defaults without changing Training truth.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            maximumInternalCompletions: 250,
            maximumProfessionalCertifications: 100,
            includeProfessionalCertificationsByDefault: false,
            transcriptPage: { format: 'letter', orientation: 'portrait', marginPreset: 'standard' },
            certificatePage: { format: 'letter', orientation: 'landscape', marginPreset: 'wide' },
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: false,
        safetyClass: 'ordinary',
    },
    valueSchema: documentsTrainingRecordsConfigurationValueSchema,
});
export const documentsTrainingRecordsDisplayValueSchema = z.strictObject({
    pageSize: z.union([z.literal(25), z.literal(50), z.literal(100)]),
    density: z.enum(['comfortable', 'compact']),
    showIntegrityHashes: z.boolean(),
    showReleaseVersion: z.boolean(),
    showCredentialNumber: z.boolean(),
    previewScale: z.union([z.literal(75), z.literal(90), z.literal(100)]),
});
export const documentsTrainingRecordsDisplaySetting = definePlatformSetting({
    definition: {
        key: 'documents.training-records.display',
        moduleId: 'documents',
        title: 'Training record document display',
        helpText: 'Controls tenant and user layout choices for retained transcripts and internal certificates.',
        configurationDomain: 'layout',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            pageSize: 50,
            density: 'comfortable',
            showIntegrityHashes: false,
            showReleaseVersion: true,
            showCredentialNumber: true,
            previewScale: 90,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: true,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: documentsTrainingRecordsDisplayValueSchema,
});
const documentsTrainingRecordKindLabelsSchema = z.strictObject({
    transcript: z.string().trim().min(1).max(80),
    internalCompletionCertificate: z.string().trim().min(1).max(80),
});
const documentsTrainingCredentialStatusLabelsSchema = z.strictObject({
    current: z.string().trim().min(1).max(60),
    expired: z.string().trim().min(1).max(60),
    nonExpiring: z.string().trim().min(1).max(60),
    superseded: z.string().trim().min(1).max(60),
    revoked: z.string().trim().min(1).max(60),
});
export const documentsTrainingRecordsCopyValueSchema = z.strictObject({
    navigationLabel: z.string().trim().min(1).max(60),
    title: z.string().trim().min(1).max(120),
    description: z.string().trim().min(1).max(400),
    createTranscriptAction: z.string().trim().min(1).max(100),
    createCertificateAction: z.string().trim().min(1).max(100),
    learnerLabel: z.string().trim().min(1).max(80),
    completedCourseLabel: z.string().trim().min(1).max(100),
    includeProfessionalLabel: z.string().trim().min(1).max(180),
    reasonLabel: z.string().trim().min(1).max(80),
    internalCompletionsHeading: z.string().trim().min(1).max(120),
    professionalCertificationsHeading: z.string().trim().min(1).max(120),
    completedOnLabel: z.string().trim().min(1).max(80),
    scoreLabel: z.string().trim().min(1).max(80),
    issuedOnLabel: z.string().trim().min(1).max(80),
    expiresOnLabel: z.string().trim().min(1).max(80),
    issuerLabel: z.string().trim().min(1).max(80),
    credentialNumberLabel: z.string().trim().min(1).max(80),
    generatedOnLabel: z.string().trim().min(1).max(80),
    loadingLabel: z.string().trim().min(1).max(120),
    savingLabel: z.string().trim().min(1).max(120),
    loadError: z.string().trim().min(1).max(240),
    mutationError: z.string().trim().min(1).max(240),
    sourceUnavailable: z.string().trim().min(1).max(240),
    emptyTitle: z.string().trim().min(1).max(120),
    emptyDescription: z.string().trim().min(1).max(300),
    recordKindLabels: documentsTrainingRecordKindLabelsSchema,
    credentialStatusLabels: documentsTrainingCredentialStatusLabelsSchema,
});
export const documentsTrainingRecordsCopySetting = definePlatformSetting({
    definition: {
        key: 'documents.copy.training-records',
        moduleId: 'documents',
        title: 'Training record document copy',
        helpText: 'Controls tenant-editable labels while the non-certification authority notices remain fixed.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            navigationLabel: 'Training records',
            title: 'Training transcripts and internal certificates',
            description: 'Create a retained printable record from exact Training facts without changing their meaning.',
            createTranscriptAction: 'Create transcript',
            createCertificateAction: 'Create internal completion certificate',
            learnerLabel: 'Learner',
            completedCourseLabel: 'Completed course',
            includeProfessionalLabel: 'Include tenant-recorded outside credentials on the transcript',
            reasonLabel: 'Reason',
            internalCompletionsHeading: 'Internal course completions',
            professionalCertificationsHeading: 'Tenant-recorded professional credentials',
            completedOnLabel: 'Completed on',
            scoreLabel: 'Final score',
            issuedOnLabel: 'Issued on',
            expiresOnLabel: 'Expires on',
            issuerLabel: 'Issuer',
            credentialNumberLabel: 'Credential number',
            generatedOnLabel: 'Record generated on',
            loadingLabel: 'Loading retained Training records…',
            savingLabel: 'Creating Training record…',
            loadError: 'Retained Training records could not be loaded.',
            mutationError: 'The Training record could not be created. Refresh and try again.',
            sourceUnavailable: 'Training facts are unavailable or you do not have permission to create this record.',
            emptyTitle: 'No retained Training records yet',
            emptyDescription: 'Choose a learner and create a transcript or an internal completion certificate.',
            recordKindLabels: {
                transcript: 'Training transcript',
                internalCompletionCertificate: 'Internal completion certificate',
            },
            credentialStatusLabels: {
                current: 'Current', expired: 'Expired', nonExpiring: 'No expiration',
                superseded: 'Superseded', revoked: 'Revoked',
            },
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: documentsTrainingRecordsCopyValueSchema,
});
export const documentsPortalEnvelopesConfigurationValueSchema = z.strictObject({
    allowTypedSignature: z.boolean(),
    allowDecline: z.boolean(),
});
export const documentsPortalEnvelopesConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'documents.portal-envelopes.configuration', moduleId: 'documents',
        title: 'Customer Portal envelope behavior',
        helpText: 'Controls scoped typed-signature and decline actions for released document envelopes.',
        configurationDomain: 'module', valueType: 'object', schemaVersion: 1,
        codedFallback: { allowTypedSignature: true, allowDecline: true },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: false, safetyClass: 'protected',
    },
    valueSchema: documentsPortalEnvelopesConfigurationValueSchema,
});
export const documentsPortalEnvelopesDisplayValueSchema = z.strictObject({
    enabled: z.boolean(),
    showAuthenticityHash: z.boolean(),
    showSignedCopyStatus: z.boolean(),
});
export const documentsPortalEnvelopesDisplaySetting = definePlatformSetting({
    definition: {
        key: 'documents.portal-envelopes.display', moduleId: 'documents',
        title: 'Customer Portal envelope display',
        helpText: 'Controls document-envelope visibility and authenticity readback.',
        configurationDomain: 'layout', valueType: 'object', schemaVersion: 1,
        codedFallback: { enabled: true, showAuthenticityHash: false, showSignedCopyStatus: true },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: false, safetyClass: 'ordinary',
    },
    valueSchema: documentsPortalEnvelopesDisplayValueSchema,
});
export const documentsPortalEnvelopesCopyValueSchema = z.strictObject({
    title: z.string().trim().min(1).max(120),
    description: z.string().trim().min(1).max(300),
    reviewAction: z.string().trim().min(1).max(100),
    signAction: z.string().trim().min(1).max(100),
    declineAction: z.string().trim().min(1).max(100),
    typedNameLabel: z.string().trim().min(1).max(100),
    consentLabel: z.string().trim().min(1).max(300),
    signedLabel: z.string().trim().min(1).max(100),
    declinedLabel: z.string().trim().min(1).max(100),
    signedCopyReadyLabel: z.string().trim().min(1).max(160),
    signedCopyPendingLabel: z.string().trim().min(1).max(160),
    authenticityLabel: z.string().trim().min(1).max(100),
    mutationError: z.string().trim().min(1).max(240),
    emptyLabel: z.string().trim().min(1).max(240),
});
export const documentsPortalEnvelopesCopySetting = definePlatformSetting({
    definition: {
        key: 'documents.copy.portal-envelopes', moduleId: 'documents',
        title: 'Customer Portal envelope copy',
        helpText: 'Controls public envelope, consent, signature, decline, authenticity, and signed-copy labels.',
        configurationDomain: 'copy', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            title: 'Documents for your review',
            description: 'Review each released document and complete any required response.',
            reviewAction: 'Open document', signAction: 'Agree and sign', declineAction: 'Decline document',
            typedNameLabel: 'Your full legal name',
            consentLabel: 'I agree to the consent statement shown with this document.',
            signedLabel: 'Signed', declinedLabel: 'Declined',
            signedCopyReadyLabel: 'Your signed copy is recorded and ready.',
            signedCopyPendingLabel: 'Your signature is recorded. The final signed copy is still being prepared.',
            authenticityLabel: 'Authenticity record',
            mutationError: 'The document changed before your response was saved. Refresh and review it again.',
            emptyLabel: 'No released documents are available for this link.',
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: false, safetyClass: 'ordinary',
    },
    valueSchema: documentsPortalEnvelopesCopyValueSchema,
});
export const documentsSettingRegistrations = [
    documentsTemplatesConfigurationSetting,
    documentsTemplatesDisplaySetting,
    documentsTemplatesCopySetting,
    documentsInstancesConfigurationSetting,
    documentsInstancesDisplaySetting,
    documentsInstancesCopySetting,
    documentsPortalEnvelopesConfigurationSetting,
    documentsPortalEnvelopesDisplaySetting,
    documentsPortalEnvelopesCopySetting,
    documentsTrainingRecordsConfigurationSetting,
    documentsTrainingRecordsDisplaySetting,
    documentsTrainingRecordsCopySetting,
    documentsProtectedFormsDisplaySetting,
    documentsProtectedFormsCopySetting,
    documentsSignedPoliciesDisplaySetting,
    documentsSignedPoliciesCopySetting,
];
//# sourceMappingURL=documents-setting-definitions.js.map